import React, { useRef } from 'react';
import { Box, TextField } from '@mui/material';
import { PIN_LENGTH } from './pinFeatureFlag';

// One masked box per digit, styled like the OTP boxes on the login page. Typing moves forward,
// Backspace on an empty box moves back, and pasting a whole PIN fills every box.
export default function PinDigitsInput({
  value,
  onChange,
  idPrefix,
  label,
  error = false,
  autoFocus = false,
  disabled = false,
  length = PIN_LENGTH,
}) {
  const inputs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const focus = (index) => inputs.current[Math.max(0, Math.min(length - 1, index))]?.focus();

  const setDigit = (index, digit) => {
    const next = [...digits];
    next[index] = digit;
    onChange(next.join(''));
  };

  const handleChange = (index, raw) => {
    const typed = raw.replace(/\D/g, '');
    if (typed.length > 1) {
      // A paste (or autofill) into one box: spread it across the boxes from here on.
      const next = [...digits];
      typed.slice(0, length - index).split('').forEach((digit, offset) => {
        next[index + offset] = digit;
      });
      onChange(next.join(''));
      focus(index + typed.length);
      return;
    }
    setDigit(index, typed);
    if (typed && index < length - 1) {
      focus(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      setDigit(index - 1, '');
      focus(index - 1);
    }
  };

  return (
    <Box
      role="group"
      aria-label={label}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: `repeat(${length}, 44px)`, sm: `repeat(${length}, 56px)` },
        gap: { xs: 1, sm: 1.5 },
        justifyContent: 'center',
      }}
    >
      {digits.map((digit, index) => (
        <TextField
          key={index}
          id={`${idPrefix}-${index}`}
          value={digit}
          type="password"
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          error={error}
          inputRef={(element) => {
            inputs.current[index] = element;
          }}
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'off',
            maxLength: length,
            'aria-label': `${label} digit ${index + 1}`,
            'data-testid': `${idPrefix}-${index}`,
            style: { textAlign: 'center', fontSize: '1.5rem', lineHeight: 1, padding: 0 },
          }}
          sx={{
            width: { xs: 44, sm: 56 },
            '& .MuiOutlinedInput-root': { height: { xs: 48, sm: 56 } },
          }}
        />
      ))}
    </Box>
  );
}
